import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { practiceApi } from '../services/api';
import Navbar from './homepage/components/Navbar';
import Footer from './homepage/components/Footer';

interface InviteInfo {
  status: 'pending' | 'already_accepted' | 'expired';
  message?: string;
  email?: string;
  role?: string;
  practice_name?: string;
  expires_at?: string;
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'Inhaber',
  admin: 'Administrator',
  coach: 'Coach',
  assistenz: 'Assistenz',
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function InvitationAccept() {
  const { token } = useParams<{ token: string }>();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Kein Einladungs-Token vorhanden.');
      setLoading(false);
      return;
    }
    practiceApi.getInviteInfo(token)
      .then((res) => setInfo(res.data))
      .catch((err) => {
        if (err.response?.status === 404) {
          setError('Diese Einladung existiert nicht oder wurde bereits geloescht.');
        } else {
          setError('Fehler beim Laden der Einladung. Bitte versuche es spaeter erneut.');
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    try {
      await practiceApi.acceptInvite(token);
      setAccepted(true);
      await refreshUser();
      setTimeout(() => navigate('/einstellungen'), 2000);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Fehler beim Annehmen der Einladung.');
    } finally {
      setAccepting(false);
    }
  };

  const cardStyle: React.CSSProperties = {
    background: 'var(--bg-card)',
    borderRadius: 16,
    border: '1px solid var(--border)',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    padding: '48px 40px',
    maxWidth: 520,
    margin: '0 auto',
    textAlign: 'center',
  };

  const headingStyle: React.CSSProperties = {
    fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
    fontSize: 24,
    fontWeight: 700,
    color: 'var(--text)',
    margin: '0 0 8px',
  };

  const textStyle: React.CSSProperties = {
    fontFamily: "'Inter', -apple-system, sans-serif",
    fontSize: 15,
    color: 'var(--text-muted)',
    margin: 0,
    lineHeight: 1.6,
  };

  const btnPrimary: React.CSSProperties = {
    fontFamily: "'Inter', -apple-system, sans-serif",
    fontSize: 15,
    fontWeight: 600,
    color: '#fff',
    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    border: 'none',
    borderRadius: 10,
    padding: '14px 32px',
    cursor: 'pointer',
    display: 'inline-block',
    textDecoration: 'none',
    transition: 'opacity 0.2s',
  };

  const btnOutline: React.CSSProperties = {
    fontFamily: "'Inter', -apple-system, sans-serif",
    fontSize: 15,
    fontWeight: 600,
    color: '#2563eb',
    background: 'var(--bg-card)',
    border: '2px solid #2563eb',
    borderRadius: 10,
    padding: '12px 32px',
    cursor: 'pointer',
    display: 'inline-block',
    textDecoration: 'none',
    transition: 'all 0.2s',
  };

  const renderContent = () => {
    // Loading state
    if (loading) {
      return (
        <div style={cardStyle}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            border: '4px solid #e5e7eb', borderTopColor: '#2563eb',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 24px',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={textStyle}>Einladung wird geladen...</p>
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <div style={cardStyle}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: '#fef2f2', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h2 style={headingStyle}>Einladung ungueltig</h2>
          <p style={{ ...textStyle, marginBottom: 32 }}>{error}</p>
          <Link to="/" style={{ ...btnPrimary, textDecoration: 'none' }}>
            Zur Startseite
          </Link>
        </div>
      );
    }

    if (!info) return null;

    // Already accepted
    if (info.status === 'already_accepted') {
      return (
        <div style={cardStyle}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: '#f0fdf4', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 style={headingStyle}>Bereits angenommen</h2>
          <p style={{ ...textStyle, marginBottom: 32 }}>{info.message}</p>
          <Link to="/login" style={{ ...btnPrimary, textDecoration: 'none' }}>
            Zum Login
          </Link>
        </div>
      );
    }

    // Expired
    if (info.status === 'expired') {
      return (
        <div style={cardStyle}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: '#fffbeb', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <h2 style={headingStyle}>Einladung abgelaufen</h2>
          <p style={{ ...textStyle, marginBottom: 32 }}>{info.message}</p>
          <p style={{ ...textStyle, fontSize: 14, marginBottom: 32 }}>
            Bitte den Praxisinhaber um eine neue Einladung bitten.
          </p>
          <Link to="/" style={{ ...btnPrimary, textDecoration: 'none' }}>
            Zur Startseite
          </Link>
        </div>
      );
    }

    // Success state after accepting
    if (accepted) {
      return (
        <div style={cardStyle}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: '#f0fdf4', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 style={headingStyle}>Willkommen im Team!</h2>
          <p style={{ ...textStyle, marginBottom: 8 }}>
            Du bist jetzt Mitglied von <strong>{info.practice_name}</strong>.
          </p>
          <p style={{ ...textStyle, fontSize: 14 }}>
            Du wirst gleich weitergeleitet...
          </p>
        </div>
      );
    }

    // Pending — main view
    return (
      <div style={cardStyle}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px',
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </div>

        <h2 style={headingStyle}>Team-Einladung</h2>
        <p style={{ ...textStyle, marginBottom: 24 }}>
          Du wurdest eingeladen, dem Team beizutreten:
        </p>

        {/* Practice info card */}
        <div style={{
          background: 'var(--bg)',
          borderRadius: 12,
          border: '1px solid var(--border)',
          padding: '20px 24px',
          marginBottom: 32,
          textAlign: 'left',
        }}>
          <div style={{ marginBottom: 12 }}>
            <span style={{
              fontFamily: "'Inter', -apple-system, sans-serif",
              fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: 0.5,
            }}>
              Praxis
            </span>
            <p style={{
              fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
              fontSize: 18, fontWeight: 700, color: 'var(--text)',
              margin: '4px 0 0',
            }}>
              {info.practice_name}
            </p>
          </div>
          <div style={{
            display: 'flex', gap: 32,
          }}>
            <div>
              <span style={{
                fontFamily: "'Inter', -apple-system, sans-serif",
                fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: 0.5,
              }}>
                Rolle
              </span>
              <p style={{
                fontFamily: "'Inter', -apple-system, sans-serif",
                fontSize: 15, fontWeight: 500, color: 'var(--text-light)',
                margin: '4px 0 0',
              }}>
                {ROLE_LABELS[info.role || ''] || info.role}
              </p>
            </div>
            <div>
              <span style={{
                fontFamily: "'Inter', -apple-system, sans-serif",
                fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: 0.5,
              }}>
                Gueltig bis
              </span>
              <p style={{
                fontFamily: "'Inter', -apple-system, sans-serif",
                fontSize: 15, fontWeight: 500, color: 'var(--text-light)',
                margin: '4px 0 0',
              }}>
                {info.expires_at ? formatDate(info.expires_at) : '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        {user ? (
          // Logged in — show accept button
          <div>
            {user.email.toLowerCase() !== (info.email || '').toLowerCase() ? (
              <div style={{
                background: '#fffbeb',
                border: '1px solid #fcd34d',
                borderRadius: 10,
                padding: '16px 20px',
                marginBottom: 24,
              }}>
                <p style={{
                  fontFamily: "'Inter', -apple-system, sans-serif",
                  fontSize: 14, color: '#92400e', margin: 0, lineHeight: 1.5,
                }}>
                  Diese Einladung gilt fuer <strong>{info.email}</strong>.
                  Du bist aktuell als <strong>{user.email}</strong> eingeloggt.
                  Bitte melde dich mit der richtigen E-Mail-Adresse an.
                </p>
              </div>
            ) : (
              <button
                onClick={handleAccept}
                disabled={accepting}
                style={{
                  ...btnPrimary,
                  width: '100%',
                  opacity: accepting ? 0.7 : 1,
                  cursor: accepting ? 'not-allowed' : 'pointer',
                }}
              >
                {accepting ? 'Wird angenommen...' : 'Einladung annehmen'}
              </button>
            )}
          </div>
        ) : (
          // Not logged in — show login/register options
          <div>
            <p style={{ ...textStyle, fontSize: 14, marginBottom: 20 }}>
              Um die Einladung anzunehmen, melde dich an oder erstelle ein Konto:
            </p>
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 12,
            }}>
              <Link
                to={`/login?redirect=/einladung/${token}`}
                style={{ ...btnPrimary, textDecoration: 'none' }}
              >
                Anmelden
              </Link>
              <Link
                to={`/register?email=${encodeURIComponent(info.email || '')}&redirect=/einladung/${token}`}
                style={{ ...btnOutline, textDecoration: 'none' }}
              >
                Neues Konto erstellen
              </Link>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="homepage" style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Navbar />

      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #1e3a5f 100%)',
        padding: '140px 24px 60px',
        textAlign: 'center',
      }}>
        <h1 style={{
          fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
          fontSize: 36,
          fontWeight: 800,
          color: '#fff',
          margin: 0,
        }}>
          Team-Einladung
        </h1>
        <p style={{
          fontFamily: "'Inter', -apple-system, sans-serif",
          fontSize: 16,
          color: 'rgba(255,255,255,0.7)',
          marginTop: 12,
        }}>
          Tritt einem CoachDesk365-Team bei
        </p>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '48px 24px 80px' }}>
        {renderContent()}

        {/* Back link */}
        <div style={{ textAlign: 'center', marginTop: 48 }}>
          <Link to="/" style={{
            fontFamily: "'Inter', -apple-system, sans-serif",
            fontSize: 14,
            color: '#2563eb',
            textDecoration: 'none',
            fontWeight: 500,
          }}>
            &larr; Zurueck zur Startseite
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
